import React, { useEffect, useState } from 'react';
import { Grid, Tooltip } from "@mui/material";
import {
    BreadCrumbs,
    Table,
    Column,
    PageTitleWithToolTip,
    AddButton,
    DataGrid
} from "../../Common";
import CreateIcon from '@mui/icons-material/Create';
import Routes from '../../../routes';
import toast from 'react-hot-toast';
import { SwitchInput } from '../../Common/SwitchInput';
import MaterialTypeModal from './MaterialTypeModal';
import { select_commonCellProps, text_commonCellProps } from '../../Common/DataGrid/utils';
import { getRangeFilterOperator } from '../../Common/DataGrid/FilterComponents';
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, GridActionsCellItem } from '@mui/x-data-grid-pro';
import { Grey2 } from '../../../color_utils';

const rangeFilterOperator = getRangeFilterOperator({});

const ManageMaterialTypes = () => {
    const [refreshNeeded, setRefreshNeeded] = useState(false);
    const [modalPayload, setModalPayload] = useState({
        id: 0,
        modalOpen: false
    });
    const [filterOptions, setFilterOptions] = useState([]);

    const toggleMaterialTransfer = async (model) => {
        try {
            const response = await fetch(Routes.MaterialTypes.ToggleMaterialType(), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    id: model.id,
                    enabled: !model.enabled
                })
            });

            if (response.status === 200) {
                toast.success(`The Material type has been successfully ${model.enabled ? 'disabled' : 'enabled'}.`, { duration: 6000 });
                setRefreshNeeded(!refreshNeeded);
            }
        } catch (error) {
            toast.error('Error updating Material type', { duration: 6000 });
        }
    }
    const columns = [
        {
            field: 'enabled',
            headerName: 'Enabled',
            sortable: false,
            ...select_commonCellProps,
            disableColumnMenu: true,
            renderHeader: () => null,
            renderCell: ({ row: { enabled, id } }) => (
                <SwitchInput
                    state={enabled}
                    hiddenLabel={true}
                    stateUpdated={() => toggleMaterialTransfer({ enabled, id })}
                />
            ),
        },
        {
            field: 'id',
            headerName: 'ID',
            ...text_commonCellProps,
        },
        {
            field: 'name',
            headerName: 'Name',
            ...text_commonCellProps,
        },
        {
            field: 'tradeName',
            headerName: 'Trade Name',
            ...text_commonCellProps,
        },
        {
            field: 'casName',
            headerName: 'CAS Name',
            ...text_commonCellProps,
        },
        {
            field: 'refrigerantTypeName',
            headerName: 'Refrigerant Type',
            ...select_commonCellProps,
        },
        {
            field: 'class',
            headerName: 'Class',
            ...select_commonCellProps,
        },
        {
            field: 'materialTypeSafetyClassName',
            headerName: 'Safety Class',
            ...select_commonCellProps,
        },
        {
            field: 'ar4100Gwp',
            headerName: 'AR4 100 GWP',
            filterOperators: rangeFilterOperator,
            flex: 1
        },
        {
            field: 'ar5100Gwp',
            headerName: 'AR5 100 GWP',
            filterOperators: rangeFilterOperator,
            flex: 1
        },
        {
            field: 'ar620Gwp',
            headerName: 'AR6 20 GWP',
            filterOperators: rangeFilterOperator,
            flex: 1
        },
        {
            field: 'containsAimRegulatedSubstanceName',
            headerName: 'Contains AIM Act Regulated Substance',
            ...select_commonCellProps,
        },
        {
            field: 'density',
            headerName: 'Density (CUFT/LB)',
            filterOperators: rangeFilterOperator,
            flex: 1
        },
        {
            field: 'gallonDensity',
            headerName: 'Density (LB/GAL)',
            filterOperators: rangeFilterOperator,
            flex: 1
        },
        {
            field: 'sortOrder',
            headerName: 'Sort Order',
            ...text_commonCellProps,
        },
        {
            field: 'accountTypeName',
            headerName: 'Account Type',
            ...select_commonCellProps,
            renderCell: ({ value }) => value || '',
        },
        {
            field: 'Quick Actions',
            headerName: '',
            type: 'actions',
            width: 60,
            sortable: false,
            filterable: false,
            disableExport: true,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={
                        <Tooltip title="Edit Registration Type">
                            <CreateIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    label="Edit"
                    onClick={() => setModalPayload({ id: params.id, modalOpen: true })}
                    showInMenu={false}
                />,
            ],
        }
    ];
    
    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch(Routes.MaterialTypes.GetMaterialTypeLookups());
            const data = await response.json();
            setFilterOptions(data);
        }

        fetchData();
    }, [])

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Geek Command Center', to: `/GeekCommandCenter` },
                    { text: 'Manage Material Types' }
                ]}
            />
            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={6} md={6} lg={6} mb={2}>
                    <PageTitleWithToolTip
                        pageTitle="Manage Material Types"
                        toolTipText="Manage material types available in the software. Material Type is the term in the software used to define the Refrigerant Type of an asset. The Material Types table reflects GWP as defined under IPCC AR4, and these values are used to assist customers in capturing their GHGP and Scope 1 emissions."
                    />
                </Grid>
                <Grid item xs={6} md={6} lg={6} align="right">
                    <AddButton
                        text="Add material type"
                        onClick={() => {
                            setModalPayload({
                                id: 0,
                                modalOpen: true
                            });
                        }}
                    />
                </Grid>
            </Grid>
            <DataGrid
                refresh={refreshNeeded}
                showColumnHeaderFilter
                filterOptions={filterOptions}
                url={Routes.MaterialTypes.GetTable()}
                exportButtonURL={Routes.MaterialTypes.ExportMaterialTypes()}
                exportButtonAlt="Export Material types (.csv)"
                noResultText="Oops... It's empty here..."
                noResultText2="Create a new material type or change your search criteria."
                columns={columns}
                getRowId={(row) => row.id}
            />

            <MaterialTypeModal
                id={modalPayload.id}
                modalOpen={modalPayload.modalOpen}
                onClose={() =>
                    setModalPayload({
                        id: 0,
                        modalOpen: false
                    })
                }
                filterOptions={filterOptions}
                refreshRequired={() => setRefreshNeeded(!refreshNeeded)}
            />
        </>
    );
}

export default ManageMaterialTypes;